/**
 * Admin Coupons Data Fetching Hooks
 *
 * Custom hooks using React Query for fetching and mutating admin coupon data.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createCoupon, createCouponsBulk, deactivateCoupon, fetchCoupons } from "../utils/adminApi";

export const COUPONS_QUERY_KEY_PREFIX = ["admin", "coupons"];

/**
 * Fetch a single cursor page for the coupons list.
 * Query key includes filters + cursor so pages are cached separately.
 */
export const useCouponsList = ({
  cursor = null,
  limit = 100,
  q = "",
  active_state = "all",
  list_version = 0,
} = {}) => {
  return useQuery({
    queryKey: [...COUPONS_QUERY_KEY_PREFIX, { cursor, limit, q, active_state, list_version }],
    queryFn: () => fetchCoupons({ cursor, limit, q, active_state }),
    refetchOnWindowFocus: false,
  });
};

/**
 * Lightweight coupon search for typeaheads (e.g. the linked-coupon picker).
 * Reuses the admin coupons list endpoint with a `q` filter.
 */
export const useCouponSearch = ({ q = "", limit = 20, enabled = true } = {}) => {
  return useQuery({
    queryKey: [...COUPONS_QUERY_KEY_PREFIX, "search", { q, limit }],
    queryFn: () => fetchCoupons({ q, limit }),
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useCreateCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createCoupon(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: COUPONS_QUERY_KEY_PREFIX });
    },
  });
};

export const useCreateCouponsBulk = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createCouponsBulk(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: COUPONS_QUERY_KEY_PREFIX });
    },
  });
};

export const useDeactivateCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (couponId) => deactivateCoupon(couponId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: COUPONS_QUERY_KEY_PREFIX });
    },
  });
};


